import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { PosTabsType } from "../features/pointOfSale/enums/PosTabsType";
import { CachedData } from "@unleashed/models/CachedData";

export type CafeCategoryFilterValue = "all" | "uncategorized" | number;

export interface PosConfigState {
  activePosTabIndex: PosTabsType;
  defaultPosTabIndex: PosTabsType;
  terminalDeviceName: string;
  confettiEnabled: boolean;
  activeCafeCategoryFilter: CafeCategoryFilterValue;
}

const CONFETTI_STORAGE_KEY = "confetti-enabled";

const getDefaultViewFromLocalStorage: () => PosTabsType = () => {
  const defaultViewAsInt: number = (() => {
    const defaultView = localStorage.getItem("DefaultView");
    if (!defaultView) return 0;

    const data = JSON.parse(defaultView) as CachedData;
    const value = parseInt(String(data.value) || "0", 10);
    return isNaN(value) ? 0 : value;
  })();
  return defaultViewAsInt as PosTabsType;
};

const getTerminalDeviceNameFromLocalStorage: () => string = () => {
  const terminalId = localStorage.getItem("TerminalId");
  if (!terminalId) return "";

  const data = JSON.parse(terminalId) as CachedData;
  return data.value as string;
};

const getConfettiEnabledFromLocalStorage: () => boolean = () => {
  const raw = localStorage.getItem(CONFETTI_STORAGE_KEY);
  if (!raw) return true;

  try {
    const parsed = JSON.parse(raw) as { value?: boolean };
    if (typeof parsed?.value === "boolean") return parsed.value;
  } catch {
    return true;
  }

  return true;
};

const initialState: PosConfigState = {
  activePosTabIndex: getDefaultViewFromLocalStorage(),
  defaultPosTabIndex: getDefaultViewFromLocalStorage(),
  terminalDeviceName: getTerminalDeviceNameFromLocalStorage(),
  confettiEnabled: getConfettiEnabledFromLocalStorage(),
  activeCafeCategoryFilter: "all",
};

export const PosConfigSlice = createSlice({
  name: "posConfig",
  initialState,
  reducers: {
    setDefaultView: (state, action: PayloadAction<PosTabsType>) => {
      const defaultView = action.payload;

      state.defaultPosTabIndex = Number(defaultView);
      state.activePosTabIndex = Number(defaultView);
      localStorage.setItem(
        "DefaultView",
        JSON.stringify({ value: defaultView })
      );
    },

    setActivePosTabIndex: (state, action: PayloadAction<PosTabsType>) => {
      state.activePosTabIndex = action.payload;
    },

    setActiveCafeCategoryFilter: (
      state,
      action: PayloadAction<CafeCategoryFilterValue>
    ) => {
      state.activeCafeCategoryFilter = action.payload;
    },

    setTerminalDeviceName: (state, action: PayloadAction<string>) => {
      const terminalDeviceName = action.payload;

      state.terminalDeviceName = terminalDeviceName;
      localStorage.setItem(
        "TerminalId",
        JSON.stringify({ value: terminalDeviceName })
      );
    },

    setConfettiEnabled: (state, action: PayloadAction<boolean>) => {
      const confettiEnabled = action.payload;

      state.confettiEnabled = confettiEnabled;
      localStorage.setItem(
        CONFETTI_STORAGE_KEY,
        JSON.stringify({ value: confettiEnabled })
      );
    },
  },
  extraReducers: (builder) => {
    // Reset the cafe category filter whenever the whole POS state is cleared.
    builder
      .addCase("pos/resetPosState", (state) => {
        state.activeCafeCategoryFilter = "all";
      })
      .addCase("pos/resetPosStateOnLocationChange", (state) => {
        state.activeCafeCategoryFilter = "all";
      });
  },
});

export const {
  setDefaultView,
  setActivePosTabIndex,
  setActiveCafeCategoryFilter,
  setTerminalDeviceName,
  setConfettiEnabled,
} = PosConfigSlice.actions;
